<template>
  <div>
    <PageHeader title="Add Budget Template" />

    <div class="px-5 pb-5 mb-5">
      <CreateBudgetItem
        id="0"
        v-model="budgetTemplate"
        hide-save-template
        @can-submit="canSubmit = $event"
      />
    </div>

    <hr class="mt-6">

    <div class="flex justify-end gap-2 my-3">
      <BaseButton
        text="Cancel"
        :disabled="isPending"
        :class="{ 'cursor-not-allowed': isPending }"
        @click="$router.push({ name: 'ListBudgetTemplates' })"
      />
      <BaseButton
        variant="primary"
        text="Save"
        :disabled="!canSubmit || isPending"
        :class="{ 'cursor-not-allowed': !canSubmit || isPending }"
        @click="createBudgetTemplate(budgetTemplate)"
      />
    </div>
  </div>

</template>

<script lang="ts" setup>
import { ref } from 'vue'
import { BudgetItem } from '@/models/budget'
import BaseButton from '@/components/base/BaseButton.vue'
import CreateBudgetItem from '@/modules/Budget/components/CreateBudgetItem.vue'
import { useMutation, useQueryClient } from '@tanstack/vue-query'
import { useRouter } from 'vue-router'
import BudgetTemplateService from '@/services/BudgetTemplateService'
import PageHeader from '@/components/app/AppPageHeader.vue'
import { useHead } from '@unhead/vue'
import { useNotification } from '@kyvg/vue3-notification'

useHead({ title: 'Add Budget Template' })

const { notify } = useNotification()
const queryClient = useQueryClient()
const $router = useRouter()
const budgetTemplate = ref({
  ...new BudgetItem(),
  laborBudget: 0,
  materialsBudget: 0,
})
const budgetTemplateService = new BudgetTemplateService()

const canSubmit = ref(false)

const { mutateAsync: createBudgetTemplate, isPending } = useMutation({
  mutationFn: (budgetTemplate: BudgetItem) => budgetTemplateService.create(budgetTemplate),
  onSuccess: (createdBudgetTemplate: BudgetItem) => {
    queryClient.setQueryData(['budgetTemplate', createdBudgetTemplate.id], createdBudgetTemplate)
    queryClient.invalidateQueries({ queryKey: ['budgetTemplates'] })
    notify({
      type: 'success',
      title: 'Budget Template created',
      text: 'Budget Template created successfully'
    })
    $router.push({ name: 'ListBudgetTemplates' })
  },
  onError: (error) => {
    notify({
      type: 'error',
      title: 'Error',
      text: error.message || 'An error occurred while trying to create the budget template'
    })
  }
})
</script>
